import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/login.css";
import { loginUser, verifyCode, forgotPassword } from "../services/login";
import { getMe } from "../services/session";
import { Eye, EyeOff } from "lucide-react";
import { registerStoredPushToken } from "../utils/pushInit";
import {
    API_BASE_OPERATOR,
    clearStoredAuthStateForServerChange,
    getDefaultApiBaseOperator,
    resetApiBaseOperator,
    setApiBaseOperator,
    shouldAllowServerSelection,
} from "../config/vars";

const SERVER_UNLOCK_KEY = "WR_OPERATOR_SERVER_SELECTION_UNLOCKED";
const SECRET_TAP_TARGET = 7;
const SECRET_TAP_WINDOW_MS = 2500;

function formatServerLabel(value) {
    if (!value) return "не налаштовано";
    try {
        return new URL(value).host;
    } catch (_) {
        return value;
    }
}

const LoginPageOperators = () => {
    const [form, setForm] = useState({ email: "", password: "" });
    const [showPassword, setShowPassword] = useState(false);
    const [code, setCode] = useState("");
    const [step, setStep] = useState(1); // 1 - логін, 2 - MFA код
    const [message, setMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [serverAccessUnlocked, setServerAccessUnlocked] = useState(() => {
        try {
            return localStorage.getItem(SERVER_UNLOCK_KEY) === "1";
        } catch (_) {
            return false;
        }
    });
    const [serverPanelOpen, setServerPanelOpen] = useState(false);
    const [serverUrl, setServerUrl] = useState(API_BASE_OPERATOR || getDefaultApiBaseOperator());
    const [serverMessage, setServerMessage] = useState("");
    const secretTapCountRef = useRef(0);
    const secretTapTimerRef = useRef(null);
    const navigate = useNavigate();
    const serverSelectionEnabled = shouldAllowServerSelection();
    const defaultServerUrl = getDefaultApiBaseOperator();
    const activeServerLabel = formatServerLabel(API_BASE_OPERATOR || defaultServerUrl);

    useEffect(() => () => {
        if (secretTapTimerRef.current) {
            window.clearTimeout(secretTapTimerRef.current);
        }
    }, []);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleCodeChange = (e) => setCode(e.target.value);

    const reloadWithNewServer = () => {
        clearStoredAuthStateForServerChange();
        window.location.reload();
    };

    const handleSaveServer = () => {
        try {
            setApiBaseOperator(serverUrl);
            reloadWithNewServer();
        } catch (error) {
            setServerMessage(error.message || "Не вдалося зберегти адресу сервера");
        }
    };

    const handleResetServer = () => {
        resetApiBaseOperator();
        setServerUrl(defaultServerUrl);
        reloadWithNewServer();
    };

    const handleToggleServerPanel = () => {
        setServerPanelOpen((prev) => {
            const next = !prev;
            if (next) {
                setServerMessage("");
            }
            return next;
        });
    };

    const handleSecretTap = () => {
        if (!serverSelectionEnabled || serverAccessUnlocked) return;

        secretTapCountRef.current += 1;
        if (secretTapTimerRef.current) {
            window.clearTimeout(secretTapTimerRef.current);
        }

        secretTapTimerRef.current = window.setTimeout(() => {
            secretTapCountRef.current = 0;
        }, SECRET_TAP_WINDOW_MS);

        if (secretTapCountRef.current < SECRET_TAP_TARGET) {
            return;
        }

        secretTapCountRef.current = 0;
        window.clearTimeout(secretTapTimerRef.current);
        secretTapTimerRef.current = null;
        try {
            localStorage.setItem(SERVER_UNLOCK_KEY, "1");
        } catch (_) {
            // ignore
        }
        setServerAccessUnlocked(true);
        setServerPanelOpen(true);
        setServerMessage("Меню сервера розблоковано на цьому пристрої");
    };

    const handleLogin = async () => {
        try {
            setLoading(true);
            setMessage("");
            const response = await loginUser(form.email, form.password);

            // Якщо MFA вимкнено — бекенд вже поставить cookies. Якщо MFA ввімкнено — cookies не буде.
            const me = await getMe();
            if (me) {
                if (me.operator_id) localStorage.setItem("operatorId", me.operator_id);
                if (me.network_id) localStorage.setItem("networkId", me.network_id);
                localStorage.setItem("authEmail", form.email);
                registerStoredPushToken().catch(() => { });
                navigate("/main");
                return;
            }

            // MFA увімкнено → чекаємо код
            setStep(2);
            setMessage(response?.message || "Код підтвердження надіслано на email");
            localStorage.setItem("authEmail", form.email);
        } catch (error) {
            setMessage(error.message || "Помилка входу");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyCode = async () => {
        try {
            setLoading(true);
            setMessage("");
            const email = localStorage.getItem("authEmail") || form.email;
            await verifyCode(email, code);

            const me = await getMe();
            if (me) {
                if (me.operator_id) localStorage.setItem("operatorId", me.operator_id);
                if (me.network_id) localStorage.setItem("networkId", me.network_id);
                registerStoredPushToken().catch(() => { });
                navigate("/main");
            } else {
                setMessage("Помилка підтвердження коду");
            }
        } catch (error) {
            setMessage(error.message || "Невірний код");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!form.email) {
            setMessage("Введіть email перед відновленням пароля");
            return;
        }

        try {
            setLoading(true);
            setMessage("");
            const data = await forgotPassword(form.email);
            setMessage(data.message || "Перевірте свою пошту для відновлення пароля");
        } catch (error) {
            setMessage(error.message || "Помилка відновлення пароля");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            <h2 onClick={handleSecretTap}>Вхід Оператора</h2>
            {serverSelectionEnabled && serverAccessUnlocked ? (
                <div className="server-config-card">
                    <button type="button" className="server-config-toggle" onClick={handleToggleServerPanel}>
                        Сервер: {activeServerLabel}
                    </button>
                    {serverPanelOpen ? (
                        <div className="server-config-panel">
                            <label htmlFor="operator-server-url">API сервер</label>
                            <input
                                id="operator-server-url"
                                type="url"
                                value={serverUrl}
                                onChange={(e) => setServerUrl(e.target.value)}
                                placeholder="https://api.example.com"
                                autoCapitalize="none"
                                autoCorrect="off"
                            />
                            <div className="server-config-actions">
                                <button type="button" onClick={handleSaveServer} disabled={loading}>
                                    Зберегти сервер
                                </button>
                                <button type="button" className="forgot-btn" onClick={handleResetServer} disabled={loading}>
                                    Сервер за замовчуванням
                                </button>
                            </div>
                            <small>За замовчуванням: {formatServerLabel(defaultServerUrl)}</small>
                            {serverMessage ? <p className="server-config-message">{serverMessage}</p> : null}
                        </div>
                    ) : null}
                </div>
            ) : null}

            {step === 1 ? (
                <div className="form-group">
                    <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        value={form.email}
                        onChange={handleChange}
                    />
                    <div className="password-input-wrapper">
                        <input
                            type={showPassword ? "text" : "password"}
                            name="password"
                            placeholder="Пароль"
                            value={form.password}
                            onChange={handleChange}
                        />
                        <button
                            type="button"
                            className="toggle-password-btn"
                            onClick={() => setShowPassword((v) => !v)}
                            title={showPassword ? "Приховати пароль" : "Показати пароль"}
                            aria-label={showPassword ? "Приховати пароль" : "Показати пароль"}
                        >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                        </button>
                    </div>
                    <div className="button-group">
                        <button onClick={handleLogin} disabled={loading}>
                            {loading ? "Зачекайте..." : "Увійти"}
                        </button>
                        <button
                            onClick={handleForgotPassword}
                            className="forgot-btn"
                            disabled={loading}
                        >
                            Забули пароль?
                        </button>
                    </div>
                </div>
            ) : (
                <div className="form-group">
                    <p>Ми відправили код підтвердження на {form.email}</p>
                    <input
                        type="text"
                        placeholder="Код з email"
                        value={code}
                        onChange={handleCodeChange}
                    />
                    <button onClick={handleVerifyCode} disabled={loading}>
                        {loading ? "Зачекайте..." : "Підтвердити код"}
                    </button>
                </div>
            )}

            {message && <p>{message}</p>}
        </div>
    );
};

export default LoginPageOperators;
